import { Component } from 'react'
import * as Sentry from '@sentry/react'

// ErrorBoundary renders above <App>, where buildThemeVars injects the CSS
// custom properties — so var(--c-*) is undefined at this layer. Pick palette
// directly from prefers-color-scheme. Hexes mirror src/theme.js exactly.
function pickPalette() {
  const darkMode = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches
  return darkMode ? {
    page:    '#1a1e1a',
    bg:      '#252b25',
    heading: '#b0c8b0',
    text:    '#c8d8c8',
    blight:  '#2d362d',
  } : {
    page:    '#f7f5f0',
    bg:      '#ffffff',
    heading: '#2d5a2d',
    text:    '#1a1a1a',
    blight:  '#f0ebe3',
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, showDetails: false }
    this.handleReset = this.handleReset.bind(this)
    this.toggleDetails = this.toggleDetails.bind(this)
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.warn('Tend: ErrorBoundary caught an error', error, info)
    Sentry.captureException(error, { extra: info })
  }

  handleReset() {
    Object.keys(localStorage)
      .filter(k => k.startsWith('tend:'))
      .forEach(k => localStorage.removeItem(k))
    window.location.reload()
  }

  toggleDetails() {
    this.setState(prev => ({ showDetails: !prev.showDetails }))
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const { error, showDetails } = this.state
    const c = pickPalette()

    return (
      <div
        data-testid="tend-error-boundary"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          background: c.page,
          fontFamily: "'Lora', Georgia, serif",
          padding: '24px',
        }}
      >
        <div
          style={{
            background: c.bg,
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              color: c.heading,
              fontSize: '24px',
              marginBottom: '12px',
              fontFamily: "'Playfair Display', Georgia, serif",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              color: c.text,
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '24px',
            }}
          >
            Tend encountered an unexpected error. You can try resetting the app
            to its default state.
          </p>
          <button
            onClick={this.handleReset}
            style={{
              background: c.heading,
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              cursor: 'pointer',
              fontFamily: "'Lora', Georgia, serif",
            }}
          >
            Reset to defaults
          </button>
          <div>
            <button
              onClick={this.toggleDetails}
              style={{
                background: 'none',
                border: 'none',
                color: c.heading,
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '16px',
                textDecoration: 'underline',
              }}
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>
          {showDetails && (
            <pre
              style={{
                background: c.blight,
                borderRadius: '8px',
                padding: '16px',
                marginTop: '12px',
                textAlign: 'left',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px',
                color: c.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error?.message}
              {'\n\n'}
              {error?.stack}
            </pre>
          )}
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
